import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, cn } from '@jeraisonne/ui';
import { CheckCircle2, AlertTriangle, Circle, ChevronRight } from 'lucide-react';
import { SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import {
  fetchDiagnosticBreakdown,
  type DiagnosticChapterRow,
  type DiagnosticChapterStatus,
} from '@/lib/student-objectives/diagnostic-breakdown';
import { Spinner } from '@/components/spinner';

const STATUS_META: Record<
  DiagnosticChapterStatus,
  { label: string; cls: string; icon: typeof CheckCircle2 }
> = {
  acquis: {
    label: 'Acquis',
    cls: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: CheckCircle2,
  },
  a_revoir: {
    label: 'À revoir',
    cls: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: AlertTriangle,
  },
  non_evalue: {
    label: 'Non évalué',
    cls: 'border-ink-200 bg-ink-50 text-ink-500',
    icon: Circle,
  },
};

interface Props {
  studentId: string;
  studentLevel: SchoolLevel;
  onStartExercise: (exerciseId: string) => void;
  /** Exercice en cours d'ouverture (spinner sur sa carte). */
  startingExerciseId?: string | null;
}

export function DiagnosticBreakdownPanel({
  studentId,
  studentLevel,
  onStartExercise,
  startingExerciseId = null,
}: Props) {
  const { data: rows, isLoading } = useQuery({
    queryKey: ['diagnostic-breakdown', studentId, studentLevel],
    enabled: !!studentId && !!studentLevel,
    staleTime: 60_000,
    queryFn: () => fetchDiagnosticBreakdown(supabase, studentId, studentLevel),
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-ink-100 h-14 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!rows || rows.length === 0) return null;

  const acquis = rows.filter((r) => r.status === 'acquis').length;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="text-ink-400 text-xs font-semibold uppercase tracking-wider">
          Bilan par chapitre
        </p>
        <span className="text-ink-400 text-xs tabular-nums">
          {acquis}/{rows.length} acquis
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {rows.map((row) => (
          <DiagnosticChapterCard
            key={row.chapterId}
            row={row}
            onStart={() => row.exerciseIds[0] && onStartExercise(row.exerciseIds[0])}
            isStarting={!!row.exerciseIds[0] && startingExerciseId === row.exerciseIds[0]}
            disabled={startingExerciseId !== null}
          />
        ))}
      </div>
    </div>
  );
}

function DiagnosticChapterCard({
  row,
  onStart,
  isStarting = false,
  disabled = false,
}: {
  row: DiagnosticChapterRow;
  onStart: () => void;
  isStarting?: boolean;
  disabled?: boolean;
}) {
  const meta = STATUS_META[row.status];
  const StatusIcon = meta.icon;
  const subjectMeta = row.subject ? SUBJECTS.find((s) => s.value === row.subject) : null;

  return (
    <button
      type="button"
      onClick={onStart}
      disabled={disabled}
      aria-busy={isStarting || undefined}
      className="w-full text-left active:scale-[0.98]"
    >
      <Card className="border-ink-100 hover:border-primary-200 transition-colors">
        <CardContent className="flex min-h-14 items-center gap-3 py-2.5">
          <span
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border',
              meta.cls,
            )}
          >
            <StatusIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-ink-900 truncate text-sm font-semibold">{row.chapterTitle}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              {subjectMeta && (
                <span className="text-ink-400 text-xs">
                  {subjectMeta.emoji} {subjectMeta.label}
                </span>
              )}
              {row.bestScore != null && (
                <span className="text-ink-400 text-xs tabular-nums">Score {row.bestScore}%</span>
              )}
            </div>
          </div>
          <span
            className={cn(
              'inline-flex h-6 shrink-0 items-center rounded-full border px-2 text-[11px] font-semibold',
              meta.cls,
            )}
          >
            {meta.label}
          </span>
          {isStarting ? (
            <Spinner className="text-primary-400 h-4 w-4" />
          ) : (
            <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" />
          )}
        </CardContent>
      </Card>
    </button>
  );
}
